import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Popover } from "antd";
import {
  CalendarOutlined,
  CheckOutlined,
  CloseOutlined,
  DownOutlined,
  SearchOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { CONTENT_TYPES } from "./content-types";
import { useContentSearch } from "./use-content-search";
import { Thumbnail } from "./thumbnail";
import {
  CUSTOM_RANGE,
  DATE_PRESETS,
  MIN_QUERY_LENGTH,
  SORTS,
  chipsOf,
  dateLabel,
  effectiveQuery,
  effectiveSort,
  splitMatch,
} from "./search-params";
import "./content-search.css";

/**
 * LF360-2030 — finding articles (and FitFacts) to attach, with filters.
 *
 * Built for every place that attaches content: the task form now, the scenario
 * editor's Suggestions and FitFacts nodes next. What it searches is the
 * `types` it is given (see content-types.js); what it hands back on Add is the
 * chosen items themselves, not ids, so the caller can show them at once.
 *
 *   open, onClose        the modal
 *   onAdd(items)         the chosen items, in the order they were picked
 *   types                content type keys, first one shown first
 *   attachedIds          already attached: shown, not selectable
 *   multiple             false picks one item on click (the editor's nodes)
 *   context              "Task · Eat your apple", shown above the search
 */

const LOAD_MORE_WITHIN_PX = 120;

/**
 * What can be picked: not attached already, and switched on. The search only
 * returns switched-on content; the status check stays so that nothing else
 * could be handed over if it ever did not — a switched-off article is not
 * delivered (LF360-2016), and a task save refuses one.
 */
const isPickable = (item, attached) => !attached.has(item.id) && item.status === "active";

// The key, or the physical key: in a Ukrainian layout the "/" key types "."
// and `key` alone never matches.
const isSlash = (e) => e.key === "/" || e.code === "Slash";

const Checkbox = ({ checked, disabled, partial }) => (
  <span
    className={[
      "lf-cs-check",
      checked || partial ? "lf-cs-check--on" : "",
      disabled ? "lf-cs-check--disabled" : "",
    ].join(" ")}
    aria-hidden
  >
    {checked && <CheckOutlined />}
    {!checked && partial && <span className="lf-cs-check__dash" />}
  </span>
);

const FilterMenu = ({ def, options, values, counts, open, onOpenChange, onToggle }) => {
  const [search, setSearch] = useState("");
  const visible = options.filter((o) => o.label.toLowerCase().includes(search.trim().toLowerCase()));

  const content = (
    <div className="lf-cs-menu" onKeyDown={(e) => e.stopPropagation()}>
      <input
        className="lf-cs-menu__search"
        autoFocus
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={`Search ${def.label.toLowerCase()}…`}
      />
      <div className="lf-cs-menu__list" role="listbox" aria-label={def.label} aria-multiselectable>
        {visible.map((o) => {
          const checked = values.includes(o.value);

          return (
            <div
              key={o.value}
              role="option"
              aria-selected={checked}
              className="lf-cs-menu__option"
              onClick={() => onToggle(o.value)}
            >
              <Checkbox checked={checked} />
              <span className="lf-cs-menu__label">{o.label}</span>
              <span className="lf-cs-count">{counts?.[o.value] ?? 0}</span>
            </div>
          );
        })}
        {!visible.length && <div className="lf-cs-menu__empty">No matches</div>}
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      open={open}
      onOpenChange={(next) => {
        if (!next) {setSearch("");}
        onOpenChange(next);
      }}
      trigger="click"
      placement="bottomLeft"
      arrow={false}
      destroyOnHidden
    >
      <button
        type="button"
        className={`lf-cs-pill ${values.length ? "lf-cs-pill--on" : ""}`}
        aria-expanded={open}
      >
        <span>{def.label}</span>
        {values.length > 0 && <span className="lf-cs-pill__badge">{values.length}</span>}
        <DownOutlined className="lf-cs-pill__chevron" />
      </button>
    </Popover>
  );
};

const DateMenu = ({ date, counts, open, onOpenChange, onChange }) => {
  const choose = (next) => onChange(next);

  const content = (
    <div className="lf-cs-menu lf-cs-menu--date" onKeyDown={(e) => e.stopPropagation()}>
      <div className="lf-cs-menu__heading">Last updated</div>
      {DATE_PRESETS.map((preset, i) => (
        <div
          key={preset.key}
          role="radio"
          aria-checked={date?.preset === preset.key}
          className="lf-cs-menu__option"
          onClick={() => {
            choose({ preset: preset.key });
            onOpenChange(false);
          }}
        >
          <span className={`lf-cs-radio ${date?.preset === preset.key ? "lf-cs-radio--on" : ""}`} />
          <span className="lf-cs-menu__label">{preset.label}</span>
          <span className="lf-cs-count">{counts?.[i] ?? ""}</span>
        </div>
      ))}
      <div className="lf-cs-menu__divider" />
      <div
        role="radio"
        aria-checked={date?.preset === CUSTOM_RANGE}
        className="lf-cs-menu__option"
        onClick={() => choose({ preset: CUSTOM_RANGE, from: date?.from || "", to: date?.to || "" })}
      >
        <span className={`lf-cs-radio ${date?.preset === CUSTOM_RANGE ? "lf-cs-radio--on" : ""}`} />
        <span className="lf-cs-menu__label">Custom range…</span>
      </div>
      {date?.preset === CUSTOM_RANGE && (
        <div className="lf-cs-range">
          <label>
            From
            <input
              type="date"
              value={date.from || ""}
              max={date.to || undefined}
              onChange={(e) => choose({ ...date, from: e.target.value })}
            />
          </label>
          <label>
            To
            <input
              type="date"
              value={date.to || ""}
              min={date.from || undefined}
              onChange={(e) => choose({ ...date, to: e.target.value })}
            />
          </label>
        </div>
      )}
      {date && (
        <button
          type="button"
          className="lf-cs-link"
          onClick={() => {
            choose(null);
            onOpenChange(false);
          }}
        >
          Any time
        </button>
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      open={open}
      onOpenChange={onOpenChange}
      trigger="click"
      placement="bottomLeft"
      arrow={false}
    >
      <button type="button" className={`lf-cs-pill ${date ? "lf-cs-pill--on" : ""}`} aria-expanded={open}>
        <CalendarOutlined />
        <span>{date ? `Updated: ${dateLabel(date)}` : "Updated"}</span>
        <DownOutlined className="lf-cs-pill__chevron" />
      </button>
    </Popover>
  );
};

const SortMenu = ({ sortKey, hasQuery, open, onOpenChange, onChange }) => {
  const current = SORTS.find((s) => s.key === sortKey);

  const content = (
    <div className="lf-cs-menu lf-cs-menu--sort">
      {SORTS.map((s) => {
        const disabled = s.key === "relevance" && !hasQuery;

        return (
          <div
            key={s.key}
            role="menuitemradio"
            aria-checked={s.key === sortKey}
            aria-disabled={disabled}
            className={`lf-cs-menu__option ${disabled ? "lf-cs-menu__option--disabled" : ""}`}
            onClick={() => {
              if (disabled) {return;}
              onChange(s.key);
              onOpenChange(false);
            }}
          >
            <span className="lf-cs-sort__text">
              <span>{s.label}</span>
              <span className="lf-cs-sort__hint">{disabled ? "Type a search term to use" : s.hint}</span>
            </span>
            {s.key === sortKey && <CheckOutlined className="lf-cs-sort__tick" />}
          </div>
        );
      })}
    </div>
  );

  return (
    <Popover
      content={content}
      open={open}
      onOpenChange={onOpenChange}
      trigger="click"
      placement="bottomRight"
      arrow={false}
    >
      <button type="button" className="lf-cs-sort" aria-expanded={open}>
        <SwapOutlined rotate={90} />
        <span className="lf-cs-sort__prefix">Sort:</span>
        <span className="lf-cs-sort__current">{current?.label}</span>
        <DownOutlined className="lf-cs-pill__chevron" />
      </button>
    </Popover>
  );
};

const ContentSearchModal = ({
  open,
  onClose,
  onAdd,
  types = ["articles"],
  attachedIds = [],
  multiple = true,
  context,
  showPreview = true,
  defaultType,
  browse = false,
  onOpenItem,
  access,
}) => {
  const search = useContentSearch({ types, open, defaultType, browse, access });
  const { type, items, facets, options, filterDefs } = search;

  const [selected, setSelected] = useState([]); // items, in the order picked
  const [active, setActive] = useState(0);
  const [menu, setMenu] = useState(null); // the open filter / date / sort menu

  const inputRef = useRef(null);
  const listRef = useRef(null);

  const attached = useMemo(() => new Set(attachedIds), [attachedIds]);
  const selectedIds = useMemo(() => new Set(selected.map((item) => item.id)), [selected]);
  const hasQuery = Boolean(effectiveQuery(search.query));
  const sortKey = effectiveSort(search.sort, search.query);
  const typing = search.query.trim().length > 0 && !hasQuery;

  const optionsByKey = options;
  const chips = chipsOf({ filters: search.filters, date: search.date }, filterDefs, optionsByKey);
  const activeIndex = Math.min(active, Math.max(items.length - 1, 0));
  const previewItem = items[activeIndex];

  const selectable = items.filter((item) => isPickable(item, attached));
  const selectedVisible = selectable.filter((item) => selectedIds.has(item.id)).length;
  const allChecked = selectable.length > 0 && selectedVisible === selectable.length;

  const focusSearch = useCallback(() => setTimeout(() => inputRef.current?.focus(), 0), []);

  // A menu that closes hands the keyboard back to the search.
  const menuToggle = (key) => (next) => {
    setMenu(next ? key : null);
    if (!next) {focusSearch();}
  };

  // A new search starts at its first result.
  useEffect(() => {
    setActive(0);
    if (listRef.current) {listRef.current.scrollTop = 0;}
  }, [search.debouncedQuery, search.filters, search.date, search.sort, search.typeKey]);

  const add = useCallback(
    (chosen) => {
      if (!chosen.length) {return;}
      onAdd(chosen);
      setSelected([]);
    },
    [onAdd]
  );

  const toggle = useCallback(
    (item) => {
      if (!item || (!browse && !isPickable(item, attached))) {return;}
      if (browse) {
        // LF360-2069: with its type — the tab it was found in, unless All named it.
        onOpenItem?.(item.contentType ? item : { ...item, contentType: search.typeKey });
        return;
      }
      if (!multiple) {
        add([item]);

        return;
      }
      setSelected((prev) =>
        prev.some((p) => p.id === item.id) ? prev.filter((p) => p.id !== item.id) : [...prev, item]
      );
    },
    [attached, multiple, add, browse, onOpenItem, search.typeKey]
  );

  const toggleAll = () =>
    setSelected((prev) => {
      if (allChecked) {
        const visible = new Set(selectable.map((item) => item.id));

        return prev.filter((item) => !visible.has(item.id));
      }
      const have = new Set(prev.map((item) => item.id));

      return [...prev, ...selectable.filter((item) => !have.has(item.id))];
    });

  const switchType = (direction) => {
    if (types.length < 2) {return;}
    const at = types.indexOf(search.typeKey);
    search.setTypeKey(types[(at + direction + types.length) % types.length]);
  };

  const scrollActiveIntoView = (index) => {
    const row = listRef.current?.querySelector(`[data-index="${index}"]`);
    row?.scrollIntoView?.({ block: "nearest" });
  };

  /**
   * The keys the footer lists. Listened for on the window while the widget is
   * open, as the design does, not on the widget's own element: focus is often
   * not inside it — clicking a row (a div) or closing a menu leaves it on the
   * page's body, and a handler on the widget then hears nothing.
   */
  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      if (menu) {
        setMenu(null);
        focusSearch();
      } else {
        onClose();
      }

      return;
    }
    // An open menu has the keyboard: its own search box, its options.
    if (menu) {return;}

    const inSearch = e.target === inputRef.current;
    // Enter on a focused button (Cancel, a filter pill) is that button's.
    if (e.key === "Enter" && !e.metaKey && !e.ctrlKey && e.target?.tagName === "BUTTON") {return;}

    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const next =
        e.key === "ArrowDown" ? Math.min(activeIndex + 1, items.length - 1) : Math.max(activeIndex - 1, 0);
      setActive(next);
      scrollActiveIntoView(next);
    } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (multiple) {add(selected);}
    } else if (e.key === "Enter") {
      e.preventDefault();
      toggle(items[activeIndex]);
    } else if ((e.key === "ArrowLeft" || e.key === "ArrowRight") && (!inSearch || !search.query)) {
      if (types.length > 1) {
        e.preventDefault();
        switchType(e.key === "ArrowLeft" ? -1 : 1);
      }
    } else if (isSlash(e) && (!inSearch || !search.query)) {
      e.preventDefault();
      if (filterDefs.length) {setMenu(filterDefs[0].key);}
    }
  };

  const keyHandler = useRef(onKeyDown);
  keyHandler.current = onKeyDown;

  useEffect(() => {
    if (!open) {return undefined;}

    const listener = (e) => keyHandler.current(e);
    window.addEventListener("keydown", listener);

    return () => window.removeEventListener("keydown", listener);
  }, [open]);

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - LOAD_MORE_WITHIN_PX) {search.loadMore();}
  };

  const removeChip = (chip) => {
    if (chip.filterKey === "updated") {
      search.setDate(null);
    } else {
      search.toggleFilterValue(chip.filterKey, chip.value);
    }
  };

  const nouns = type.noun;
  const resultLabel =
    facets.total === null || facets.total === undefined
      ? ""
      : `${facets.total} ${facets.total === 1 ? "result" : "results"}`;
  const emptyMessage = hasQuery
    ? `Nothing in ${type.label} matches “${search.query.trim()}”${chips.length ? " with the current filters" : ""}. Try a shorter term or remove a filter.`
    : `No ${nouns.many} match the current filters.`;

  const preview = showPreview && previewItem ? type.previewOf(previewItem) : null;
  const n = selected.length;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={showPreview ? 980 : 720}
      className="lf-cs-modal"
      destroyOnHidden
      // Esc is handled with the other keys, so that it closes an open menu first.
      keyboard={false}
      afterOpenChange={(visible) => visible && focusSearch()}
      styles={{ body: { padding: 0 } }}
      style={{ top: 72 }}
    >
      <div className="lf-cs">
        <div className="lf-cs__head">
          {context && (
            <div className="lf-cs__context">
              <span>Attach to</span>
              <span className="lf-cs__context-name">{context}</span>
            </div>
          )}

          <div className="lf-cs__search">
            <SearchOutlined className="lf-cs__search-icon" />
            <input
              ref={inputRef}
              data-testid="spotlight-query"
              autoFocus
              value={search.query}
              onChange={(e) => search.setQuery(e.target.value)}
              placeholder={`Search ${type.noun.many} by ${type.searchedField}…`}
              aria-label={`Search ${type.noun.many}`}
            />
            {/* A cross inside a search field reads as "clear it", so that is what it
                does; the widget closes with Cancel, Esc or a click outside it. */}
            {search.query && (
              <button
                type="button"
                className="lf-cs__clear"
                onClick={() => {
                  search.setQuery("");
                  focusSearch();
                }}
                aria-label="Clear search"
              >
                <CloseOutlined />
              </button>
            )}
          </div>

          {types.length > 1 && (
            <div className="lf-cs__types" role="tablist">
              {types.map((key) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={key === search.typeKey}
                  className={`lf-cs__type ${key === search.typeKey ? "lf-cs__type--on" : ""}`}
                  onClick={() => search.setTypeKey(key)}
                >
                  {CONTENT_TYPES[key].label}
                  {key === search.typeKey && resultLabel && (
                    <span className="lf-cs-count">{facets.total}</span>
                  )}
                </button>
              ))}
            </div>
          )}

          <div className="lf-cs__filters">
            {filterDefs.map((def) => (
              <FilterMenu
                key={`${search.typeKey}:${def.key}`}
                def={def}
                options={optionsByKey[def.key] || []}
                values={search.filters[def.key] || []}
                counts={facets.counts?.[def.key]}
                open={menu === def.key}
                onOpenChange={menuToggle(def.key)}
                onToggle={(value) => search.toggleFilterValue(def.key, value)}
              />
            ))}
            {type.supportsDate !== false && <DateMenu
              date={search.date}
              counts={facets.updated}
              open={menu === "updated"}
              onOpenChange={menuToggle("updated")}
              onChange={search.setDate}
            />}
            <span className="lf-cs__spacer" />
            <SortMenu
              sortKey={sortKey}
              hasQuery={hasQuery}
              open={menu === "sort"}
              onOpenChange={menuToggle("sort")}
              onChange={search.setSort}
            />
          </div>
        </div>

        {chips.length > 0 && (
          <div className="lf-cs__chips">
            {chips.map((chip) => (
              <span key={chip.key} className="lf-cs-chip">
                <span className="lf-cs-chip__text">
                  <strong>{chip.dimension}:</strong> <span>{chip.label}</span>
                </span>
                <span className="lf-cs-chip__divider" />
                <button
                  type="button"
                  className="lf-cs-chip__remove"
                  onClick={() => removeChip(chip)}
                  aria-label={`Remove ${chip.dimension}: ${chip.label}`}
                >
                  <CloseOutlined />
                </button>
              </span>
            ))}
            <button type="button" className="lf-cs-link" onClick={search.clearAll}>
              Clear all
            </button>
          </div>
        )}

        <div className="lf-cs__body">
          <div className="lf-cs__results" ref={listRef} onScroll={onScroll}>
            <div className="lf-cs__results-head">
              {!browse && multiple && selectable.length > 0 && (
                <button type="button" className="lf-cs__select-all" onClick={toggleAll}>
                  <Checkbox checked={allChecked} partial={selectedVisible > 0 && !allChecked} />
                  <span>{allChecked ? "Deselect all" : `Select all ${selectable.length}`}</span>
                </button>
              )}
              <span className="lf-cs__spacer" />
              {typing && (
                <span className="lf-cs__hint">Type at least {MIN_QUERY_LENGTH} characters to search</span>
              )}
              {!typing && <span>{resultLabel}</span>}
            </div>

            {items.map((item, index) => {
              const isAttached = attached.has(item.id);
              const unavailable = !browse && !isPickable(item, attached);
              const checked = selectedIds.has(item.id);
              const [before, match, after] = splitMatch(type.titleOf(item), search.debouncedQuery);
              // LF360-2069: a category only for a type whose items have one, and the
              // separators only between the parts that are there.
              const itemType = CONTENT_TYPES[item.contentType] || type;
              const meta = [
                itemType.hasCategory !== false && {
                  key: "category", className: "lf-cs-row__category", text: item.category?.name || "No category",
                },
                item.author?.name && { key: "author", text: item.author.name },
                item.updatedAt && { key: "updated", text: new Date(item.updatedAt).toLocaleDateString() },
                item.contentType && { key: "type", text: CONTENT_TYPES[item.contentType].label },
                isAttached && { key: "attached", className: "lf-cs-row__attached", text: "Already attached" },
              ].filter(Boolean);

              return (
                <div
                  key={`${item.contentType || search.typeKey}:${item.id}`}
                  data-index={index}
                  data-testid={`spotlight-result-${item.id}`}
                  role="option"
                  aria-selected={checked || isAttached}
                  aria-disabled={unavailable}
                  className={[
                    "lf-cs-row",
                    index === activeIndex ? "lf-cs-row--active" : "",
                    checked ? "lf-cs-row--checked" : "",
                    unavailable ? "lf-cs-row--attached" : "",
                  ].join(" ")}
                  onMouseMove={() => index !== activeIndex && setActive(index)}
                  onClick={() => {
                    setActive(index);
                    toggle(item);
                  }}
                >
                  {!browse && multiple && <Checkbox checked={checked || isAttached} disabled={unavailable} />}
                  <Thumbnail thumbnail={type.thumbnailOf(item)} />
                  <div className="lf-cs-row__main">
                    <div className="lf-cs-row__title">
                      {before}
                      {match && <mark>{match}</mark>}
                      {after}
                    </div>
                    <div className="lf-cs-row__meta">
                      {meta.map((part, partIndex) => (
                        <span key={part.key} className={part.className}>
                          {partIndex ? `· ${part.text}` : part.text}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}

            {!items.length && !search.loading && !search.error && (
              <div className="lf-cs-empty">
                <div className="lf-cs-empty__icon">
                  <SearchOutlined />
                </div>
                <div className="lf-cs-empty__title">No results</div>
                <div className="lf-cs-empty__text">{emptyMessage}</div>
                {chips.length > 0 && (
                  <button type="button" className="lf-cs-button" onClick={search.clearAll}>
                    Clear filters
                  </button>
                )}
              </div>
            )}

            {search.error && !items.length && (
              <div className="lf-cs-empty">
                <div className="lf-cs-empty__title" data-testid="spotlight-error">Search failed</div>
                <div className="lf-cs-empty__text">Something went wrong. Change the search to try again.</div>
              </div>
            )}

            {search.loading && <div className="lf-cs__loading">Loading…</div>}
          </div>

          {preview && (
            <aside className="lf-cs-preview" aria-label="Preview">
              <div className="lf-cs-preview__image">
                <Thumbnail thumbnail={type.thumbnailOf(previewItem)} size={132} />
              </div>
              <div className="lf-cs-preview__title">{type.titleOf(previewItem)}</div>
              {preview.excerpt && <div className="lf-cs-preview__excerpt">{preview.excerpt}</div>}
              {preview.tags.length > 0 && (
                <div className="lf-cs-preview__tags">
                  {preview.tags.map((tag) => (
                    <span key={tag}>#{tag}</span>
                  ))}
                </div>
              )}
              <dl className="lf-cs-preview__rows">
                {preview.rows.map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            </aside>
          )}
        </div>

        <div className="lf-cs__foot">
          <span className="lf-cs-keys">
            Navigate <kbd>↑</kbd>
            <kbd>↓</kbd>
          </span>
          {types.length > 1 && (
            <span className="lf-cs-keys">
              Type <kbd>←</kbd>
              <kbd>→</kbd>
            </span>
          )}
          <span className="lf-cs-keys">
            Filters <kbd>/</kbd>
          </span>
          <span className="lf-cs-keys">
            Close <kbd>Esc</kbd>
          </span>
          <span className="lf-cs__spacer" />
          {!browse && multiple && (
            <>
              {n > 0 && <span className="lf-cs__selected">{n} selected</span>}
              <button type="button" className="lf-cs-button" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="lf-cs-button lf-cs-button--primary"
                disabled={!n}
                onClick={() => add(selected)}
              >
                {n ? `Add ${n} ${n === 1 ? nouns.one : nouns.many}` : "Add"}
                <span className="lf-cs-button__keys">⌘↵</span>
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ContentSearchModal;
